import React from 'react';
import { MapPin, FileText, Check, CheckCheck, Clock, AlertTriangle, RotateCw, Phone, Link as LinkIcon, Reply } from 'lucide-react';
import { failureReason } from './chatUtils';

const SOURCE_LABELS = { bulk: 'Campaign', auto_reply: 'Automation', workflow: 'Workflow', api: 'API' };

function StatusTick({ status }) {
  if (status === 'failed') return <AlertTriangle size={13} className="text-[#BA0517]" aria-label="Failed" />;
  if (status === 'read') return <CheckCheck size={14} className="text-[#53BDEB]" aria-label="Read" />;
  if (status === 'delivered') return <CheckCheck size={14} className="text-[#706E6B]" aria-label="Delivered" />;
  if (status === 'sent') return <Check size={14} className="text-[#706E6B]" aria-label="Sent" />;
  return <Clock size={12} className="text-[#C9C9C9]" aria-label="Sending" />;
}

function MediaContent({ msg }) {
  const { messageType, mediaUrl, metadata = {} } = msg;

  if (messageType === 'image' || messageType === 'sticker') {
    return mediaUrl
      ? <img src={mediaUrl} alt="" className="max-w-xs rounded-lg" />
      : <span className="text-xs opacity-70 italic">[{messageType === 'sticker' ? 'Sticker' : 'Image'} unavailable]</span>;
  }
  if (messageType === 'video') {
    return mediaUrl
      ? <video controls src={mediaUrl} className="max-w-xs rounded-lg" />
      : <span className="text-xs opacity-70 italic">[Video unavailable]</span>;
  }
  if (messageType === 'audio') {
    return mediaUrl
      ? <audio controls src={mediaUrl} className="max-w-xs" />
      : <span className="text-xs opacity-70 italic">[Voice note unavailable]</span>;
  }
  if (messageType === 'document') {
    return mediaUrl ? (
      <a href={mediaUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm underline">
        <FileText size={16} /> {metadata.document?.filename || 'Document'}
      </a>
    ) : <span className="text-xs opacity-70 italic">[Document unavailable]</span>;
  }
  if (messageType === 'location' && metadata.location) {
    const { latitude, longitude, name } = metadata.location;
    return (
      <a href={`https://www.google.com/maps?q=${latitude},${longitude}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm underline">
        <MapPin size={16} /> {name || 'View Location'}
      </a>
    );
  }
  return null;
}

const BUTTON_ICONS = { PHONE_NUMBER: Phone, URL: LinkIcon, QUICK_REPLY: Reply };

// What the customer saw: header, body, footer and the template's buttons.
function TemplateContent({ msg }) {
  const t = msg.templateRendered;
  if (!t) {
    // Template deleted before we could snapshot it (or a very old row).
    return (
      <div className="text-sm">
        <p className="opacity-80">📋 Template: {msg.templateName}</p>
        <p className="text-xs text-[#706E6B] italic mt-0.5">Template content is no longer available.</p>
      </div>
    );
  }
  const headerMedia = t.header?.media;
  return (
    <div className="min-w-[12rem]">
      <p className="text-[10px] uppercase tracking-wide text-[#706E6B] mb-1">📋 {msg.templateName}</p>
      {t.header?.text && <p className="text-sm font-semibold mb-1">{t.header.text}</p>}
      {t.header && !t.header.text && (
        headerMedia?.type === 'image' && headerMedia.link
          ? <img src={headerMedia.link} alt="" className="max-w-xs rounded-lg mb-1" />
          : <p className="text-xs italic text-[#706E6B] mb-1 flex items-center gap-1">
              <FileText size={13} /> {headerMedia?.filename || `${(t.header.format || 'media').toLowerCase()} attachment`}
            </p>
      )}
      {t.body && <p className="text-sm leading-relaxed whitespace-pre-wrap">{t.body}</p>}
      {t.footer && <p className="text-xs text-[#706E6B] mt-1">{t.footer}</p>}
      {t.buttons?.length > 0 && (
        <div className="mt-2 -mx-3 border-t border-black/10">
          {t.buttons.map((b, i) => {
            const Icon = BUTTON_ICONS[b.type] || Reply;
            return (
              <div key={i} className="flex items-center justify-center gap-1.5 py-1.5 text-sm text-[#0176D3] border-b border-black/5 last:border-b-0">
                <Icon size={13} /> {b.text}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InboundSpecial({ msg }) {
  const meta = msg.metadata || {};
  if (msg.messageType === 'reaction') {
    return <p className="text-sm">Reacted {meta.reaction?.emoji || '👍'} to a message</p>;
  }
  if (msg.messageType === 'contacts') {
    const contacts = meta.contacts || [];
    return (
      <div className="text-sm">
        {contacts.length === 0 && <span className="italic opacity-70">[Contact card]</span>}
        {contacts.map((c, i) => (
          <p key={i}>👤 {c.name?.formatted_name || 'Contact'}{c.phones?.[0]?.phone ? ` · ${c.phones[0].phone}` : ''}</p>
        ))}
      </div>
    );
  }
  if (msg.messageType === 'order') {
    const items = meta.order?.product_items || [];
    return <p className="text-sm">🛒 Order · {items.length} item{items.length === 1 ? '' : 's'}</p>;
  }
  if (msg.messageType === 'button' || msg.messageType === 'interactive') {
    return (
      <p className="text-sm">
        <span className="text-[10px] uppercase tracking-wide text-[#706E6B] block">Tapped</span>
        {msg.messageText || <span className="italic opacity-70">[button reply]</span>}
      </p>
    );
  }
  if (msg.messageType === 'unsupported' || msg.messageType === 'unknown' || msg.messageType === 'system') {
    return <p className="text-sm italic opacity-70">{msg.messageText || '[Message type not supported in this view]'}</p>;
  }
  return null;
}

const SPECIAL_TYPES = ['reaction', 'contacts', 'order', 'button', 'interactive', 'unsupported', 'unknown', 'system'];
const MEDIA_TYPES = ['image', 'video', 'audio', 'document', 'sticker', 'location'];

export default function MessageBubble({ msg, onRetry, retrying }) {
  const isOut = msg.direction === 'outbound' || (msg.status !== 'received' && msg.direction !== 'inbound');
  const isMedia = MEDIA_TYPES.includes(msg.messageType);
  const isSpecial = !isOut && SPECIAL_TYPES.includes(msg.messageType);
  const isTemplate = msg.messageType === 'template';
  const failed = isOut && msg.status === 'failed';
  const reason = failed ? failureReason(msg) : null;
  const origin = isOut && SOURCE_LABELS[msg.source];

  return (
    <div className={`flex flex-col mb-3 ${isOut ? 'items-end' : 'items-start'}`}>
      <div className={`rounded-lg px-3 py-2 shadow-sm max-w-sm ${failed ? 'bg-[#FDE7E9] border border-[#F6A3AB] text-[#2E2E2E]' : isOut ? 'bg-[#D9FDD3] text-[#111]' : 'bg-white border border-[#E5E5E5] text-[#2E2E2E]'}`}>
        {isMedia && <MediaContent msg={msg} />}
        {isTemplate && <TemplateContent msg={msg} />}
        {isSpecial && <InboundSpecial msg={msg} />}
        {!isMedia && !isTemplate && !isSpecial && (
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.messageText}</p>
        )}
      </div>

      {failed && (
        <div className="mt-1 max-w-sm text-xs text-[#BA0517] px-1">
          <p className="font-medium">Not delivered{reason.code ? ` (error ${reason.code})` : ''}</p>
          {(reason.help || reason.detail) && <p className="text-[#BA0517]">{reason.help || reason.detail}</p>}
          {isTemplate && msg.templateStatus && msg.templateStatus !== 'APPROVED' && (
            <p className="text-[#BA0517]">
              Template is {msg.templateStatus.toLowerCase()}{msg.templateStatusReason ? ` — ${msg.templateStatusReason}` : ''}.
            </p>
          )}
          {onRetry && ['text', 'template'].includes(msg.messageType) && (
            <button
              onClick={() => onRetry(msg._id)}
              disabled={retrying}
              className="mt-1 inline-flex items-center gap-1 text-[#0176D3] hover:underline disabled:opacity-50"
            >
              <RotateCw size={11} /> {retrying ? 'Retrying…' : 'Retry'}
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-1.5 mt-0.5 px-1">
        {(origin || msg.campaignName) && (
          <span className="text-[10px] bg-[#F3F2F2] text-[#706E6B] rounded px-1.5 py-0.5">
            {msg.campaignName ? `Campaign · ${msg.campaignName}` : origin}
          </span>
        )}
        <span className="text-xs text-[#706E6B]">
          {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
        {isOut && <StatusTick status={msg.status} />}
      </div>
    </div>
  );
}
